/**
 * Database Backup Service
 * Safe read-only backup functionality for Supabase
 * NEVER modifies or deletes data - only reads and exports
 */

import { supabase } from './customSupabaseClient.js';

class DatabaseBackupService {
  constructor() {
    this.tables = [
      'bookings',
      'reviews',
      'fleet',
      'services',
      'website_content',
      'learned_locations',
      // Email Marketing Tables
      'email_subscribers',
      'email_campaigns',
      'email_templates',
      'email_automation',
      'email_automation_steps',
      'email_events',
      'email_lists',
      'email_list_subscribers',
      // Server Stats Tables
      'server_stats',
      'api_usage_logs'
    ];
    
    this.backupHistory = this.loadBackupHistory();
  }

  /**
   * Get all tables that exist in the database
   */
  async getAvailableTables() {
    try {
      const availableTables = [];
      
      // Check each table individually by trying to query it
      // In Supabase, we can't query information_schema.tables directly
      for (const tableName of this.tables) {
        try {
          const { error } = await supabase
            .from(tableName)
            .select('*')
            .limit(1);
          
          if (!error) {
            availableTables.push(tableName);
          }
        } catch (tableError) {
          // Table doesn't exist or is not accessible
          console.warn(`Table ${tableName} is not accessible:`, tableError.message);
        }
      }

      return availableTables.length > 0 ? availableTables : this.tables; // Fallback to known tables
    } catch (error) {
      console.error('Error in getAvailableTables:', error);
      return this.tables; // Fallback to known tables
    }
  }

  /**
   * Dynamically discover all tables in the database
   * This method tries to detect tables by attempting common table names
   */
  async discoverAllTables() {
    try {
      const discoveredTables = [];
      
      // Extended list of potential table names to check
      const potentialTables = [
        // Core business tables
        'bookings', 'reviews', 'fleet', 'services', 'website_content', 'learned_locations',
        
        // Email marketing tables
        'email_subscribers', 'email_campaigns', 'email_templates', 'email_automation',
        'email_automation_steps', 'email_events', 'email_lists', 'email_list_subscribers',
        
        // Analytics and performance tables
        'server_stats', 'api_usage_logs', 'performance_metrics', 'analytics_data',
        
        // User and authentication tables
        'users', 'profiles', 'user_profiles', 'auth_users', 'user_sessions',
        
        // Content management tables
        'pages', 'posts', 'articles', 'content', 'media', 'uploads',
        
        // E-commerce tables
        'orders', 'products', 'inventory', 'payments', 'transactions',
        
        // Communication tables
        'messages', 'notifications', 'alerts', 'logs', 'audit_logs',
        
        // Configuration tables
        'settings', 'config', 'preferences', 'options', 'metadata'
      ];

      console.log('🔍 Discovering tables in database...');
      
      for (const tableName of potentialTables) {
        try {
          const { error, count } = await supabase
            .from(tableName)
            .select('*', { count: 'exact', head: true })
            .limit(1);
          
          if (!error) {
            discoveredTables.push({
              name: tableName,
              count: count || 0,
              accessible: true
            });
            console.log(`✅ Found table: ${tableName} (${count || 0} records)`);
          }
        } catch (tableError) {
          // Table doesn't exist or is not accessible
          console.log(`❌ Table ${tableName} not accessible: ${tableError.message}`);
        }
      }

      // Update the known tables list with discovered tables
      const tableNames = discoveredTables.map(t => t.name);
      this.tables = [...new Set([...this.tables, ...tableNames])];
      
      console.log(`🎉 Discovery complete! Found ${discoveredTables.length} accessible tables`);
      
      return {
        tables: discoveredTables,
        total: discoveredTables.length,
        lastDiscovered: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error discovering tables:', error);
      return {
        tables: [],
        total: 0,
        error: error.message,
        lastDiscovered: new Date().toISOString()
      };
    }
  }

  /**
   * Refresh and synchronize table list
   */
  async refreshTables() {
    try {
      console.log('🔄 Refreshing table list...');
      const discovery = await this.discoverAllTables();
      
      // Save discovery results
      const refreshData = {
        timestamp: new Date().toISOString(),
        discoveredTables: discovery.tables,
        totalTables: discovery.total,
        success: !discovery.error
      };
      
      // Store refresh history
      const refreshHistory = JSON.parse(localStorage.getItem('backupRefreshHistory') || '[]');
      refreshHistory.unshift(refreshData);
      refreshHistory.splice(10); // Keep only last 10 refreshes
      localStorage.setItem('backupRefreshHistory', JSON.stringify(refreshHistory));
      
      return discovery;
    } catch (error) {
      console.error('Error refreshing tables:', error);
      return {
        tables: [],
        total: 0,
        error: error.message,
        lastDiscovered: new Date().toISOString()
      };
    }
  }

  /**
   * Get table schema information
   * Since we can't query information_schema.columns in Supabase,
   * we'll return a basic schema structure
   */
  async getTableSchema(tableName) {
    try {
      // In Supabase, we can't query information_schema.columns directly
      // Instead, we'll return a basic schema structure
      // This is a simplified approach for backup purposes
      return [
        {
          column_name: 'id',
          data_type: 'uuid',
          is_nullable: 'NO',
          column_default: 'gen_random_uuid()'
        },
        {
          column_name: 'created_at',
          data_type: 'timestamp with time zone',
          is_nullable: 'YES',
          column_default: 'now()'
        },
        {
          column_name: 'updated_at',
          data_type: 'timestamp with time zone',
          is_nullable: 'YES',
          column_default: 'now()'
        }
      ];
    } catch (error) {
      console.error(`Error in getTableSchema for ${tableName}:`, error);
      return null;
    }
  }

  /**
   * Get row count for a table
   */
  async getTableRowCount(tableName) {
    try {
      const { count, error } = await supabase
        .from(tableName)
        .select('*', { count: 'exact', head: true });

      if (error) {
        console.error(`Error getting row count for ${tableName}:`, error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error(`Error in getTableRowCount for ${tableName}:`, error);
      return 0;
    }
  }

  /**
   * Export table data with pagination for large tables
   */
  async exportTableData(tableName, options = {}) {
    const { limit = 1000, offset = 0, format = 'json' } = options;
    
    try {
      // Get total count first
      const totalCount = await this.getTableRowCount(tableName);
      
      if (totalCount === 0) {
        return {
          data: [],
          totalCount: 0,
          hasMore: false,
          tableName
        };
      }

      // Fetch data with pagination
      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .range(offset, offset + limit - 1)
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(`Error fetching data from ${tableName}: ${error.message}`);
      }

      const hasMore = (offset + limit) < totalCount;

      return {
        data: data || [],
        totalCount,
        hasMore,
        tableName,
        offset,
        limit
      };
    } catch (error) {
      console.error(`Error in exportTableData for ${tableName}:`, error);
      throw error;
    }
  }

  /**
   * Export all table data
   */
  async exportAllTables(options = {}) {
    const { format = 'json', includeSchema = true } = options;
    const availableTables = await this.getAvailableTables();
    const backupData = {
      metadata: {
        timestamp: new Date().toISOString(),
        format,
        version: '1.0',
        tables: availableTables
      },
      schema: {},
      data: {}
    };

    try {
      // Export schema information
      if (includeSchema) {
        for (const tableName of availableTables) {
          const schema = await this.getTableSchema(tableName);
          if (schema) {
            backupData.schema[tableName] = schema;
          }
        }
      }

      // Export data for each table
      for (const tableName of availableTables) {
        try {
          const tableData = await this.exportTableData(tableName, options);
          backupData.data[tableName] = tableData;
        } catch (error) {
          console.error(`Failed to export ${tableName}:`, error);
          backupData.data[tableName] = {
            error: error.message,
            data: [],
            totalCount: 0
          };
        }
      }

      return backupData;
    } catch (error) {
      console.error('Error in exportAllTables:', error);
      throw error;
    }
  }

  /**
   * Convert data to different formats
   */
  convertToFormat(data, format) {
    switch (format.toLowerCase()) {
      case 'csv':
        return this.convertToCSV(data);
      case 'sql':
        return this.convertToSQL(data);
      case 'json':
      default:
        return JSON.stringify(data, null, 2);
    }
  }

  /**
   * Convert data to CSV format
   */
  convertToCSV(backupData) {
    let csvContent = '';
    
    // Add metadata
    csvContent += `# Database Backup - ${backupData.metadata.timestamp}\n`;
    csvContent += `# Tables: ${backupData.metadata.tables.join(', ')}\n\n`;

    // Convert each table to CSV
    for (const [tableName, tableData] of Object.entries(backupData.data)) {
      if (tableData.error) {
        csvContent += `# Error exporting ${tableName}: ${tableData.error}\n\n`;
        continue;
      }

      if (tableData.data.length === 0) {
        csvContent += `# Table ${tableName} is empty\n\n`;
        continue;
      }

      csvContent += `# Table: ${tableName}\n`;
      
      // Get headers from first row
      const headers = Object.keys(tableData.data[0]);
      csvContent += headers.join(',') + '\n';
      
      // Add data rows
      tableData.data.forEach(row => {
        const values = headers.map(header => {
          const value = row[header];
          if (value === null || value === undefined) return '';
          if (typeof value === 'string' && value.includes(',')) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        });
        csvContent += values.join(',') + '\n';
      });
      
      csvContent += '\n';
    }

    return csvContent;
  }

  /**
   * Convert data to SQL format
   */
  convertToSQL(backupData) {
    let sqlContent = '';
    
    // Add header
    sqlContent += `-- Database Backup - ${backupData.metadata.timestamp}\n`;
    sqlContent += `-- Tables: ${backupData.metadata.tables.join(', ')}\n\n`;
    sqlContent += `-- WARNING: This is a data export only. Schema creation is not included.\n`;
    sqlContent += `-- Use this data with caution and test in a development environment first.\n\n`;

    // Convert each table to SQL INSERT statements
    for (const [tableName, tableData] of Object.entries(backupData.data)) {
      if (tableData.error) {
        sqlContent += `-- Error exporting ${tableName}: ${tableData.error}\n\n`;
        continue;
      }

      if (tableData.data.length === 0) {
        sqlContent += `-- Table ${tableName} is empty\n\n`;
        continue;
      }

      sqlContent += `-- Table: ${tableName}\n`;
      
      // Get headers from first row
      const headers = Object.keys(tableData.data[0]);
      
      // Create INSERT statements
      tableData.data.forEach(row => {
        const values = headers.map(header => {
          const value = row[header];
          if (value === null || value === undefined) return 'NULL';
          if (typeof value === 'string') {
            return `'${value.replace(/'/g, "''")}'`;
          }
          if (typeof value === 'boolean') {
            return value ? 'TRUE' : 'FALSE';
          }
          if (value instanceof Date) {
            return `'${value.toISOString()}'`;
          }
          return value;
        });
        
        sqlContent += `INSERT INTO ${tableName} (${headers.join(', ')}) VALUES (${values.join(', ')});\n`;
      });
      
      sqlContent += '\n';
    }

    return sqlContent;
  }

  /**
   * Download backup as file
   */
  downloadBackup(data, filename, format) {
    const content = this.convertToFormat(data, format);
    const mimeType = this.getMimeType(format);
    const blob = new Blob([content], { type: mimeType });
    
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Get MIME type for format
   */
  getMimeType(format) {
    switch (format.toLowerCase()) {
      case 'csv':
        return 'text/csv';
      case 'sql':
        return 'text/sql';
      case 'json':
      default:
        return 'application/json';
    }
  }

  /**
   * Generate filename with timestamp
   */
  generateFilename(format, prefix = 'backup') {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0];
    const extension = format.toLowerCase();
    return `${prefix}_${timestamp}.${extension}`;
  }

  /**
   * Save backup to history
   */
  saveBackupToHistory(backupInfo) {
    const backup = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      filename: backupInfo.filename,
      format: backupInfo.format,
      tableCount: backupInfo.tableCount,
      totalRecords: backupInfo.totalRecords,
      size: backupInfo.size
    };

    this.backupHistory.unshift(backup);
    
    // Keep only last 10 backups
    if (this.backupHistory.length > 10) {
      this.backupHistory = this.backupHistory.slice(0, 10);
    }

    localStorage.setItem('db_backup_history', JSON.stringify(this.backupHistory));
  }

  /**
   * Load backup history
   */
  loadBackupHistory() {
    try {
      const history = localStorage.getItem('db_backup_history');
      return history ? JSON.parse(history) : [];
    } catch (error) {
      console.error('Error loading backup history:', error);
      return [];
    }
  }

  /**
   * Get backup history
   */
  getBackupHistory() {
    return this.backupHistory;
  }

  /**
   * Clear backup history
   */
  clearBackupHistory() {
    this.backupHistory = [];
    localStorage.removeItem('db_backup_history');
  }

  /**
   * Get database statistics
   */
  async getDatabaseStats() {
    const availableTables = await this.getAvailableTables();
    const stats = {
      totalTables: availableTables.length,
      tableStats: {}
    };

    let totalRecords = 0;

    for (const tableName of availableTables) {
      try {
        const rowCount = await this.getTableRowCount(tableName);
        stats.tableStats[tableName] = {
          rowCount,
          status: 'available'
        };
        totalRecords += rowCount;
      } catch (error) {
        stats.tableStats[tableName] = {
          rowCount: 0,
          status: 'error',
          error: error.message
        };
      }
    }

    stats.totalRecords = totalRecords;
    return stats;
  }
}

// Export singleton instance
export default new DatabaseBackupService();
